import { useState, useEffect } from "react";
import { Radio, Lock, Crown } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import ImmersivePlayer from "@/components/ImmersivePlayer";
import LiveStreamPaymentModal from "@/components/LiveStreamPaymentModal";
import ClipRequestForm from "@/components/ClipRequestForm";
import YoutubeSubscribe from "@/components/YoutubeSubscribe";
import { useRef } from "react";
import { getDeviceId } from "@/utils/deviceId";

interface LiveStream {
 id: string;
 title: string;
 youtube_url: string | null;
 is_active: boolean;
 is_paid: boolean;
 price: number;
 price_subscriber: number;
 price_non_subscriber: number;
}

const extractYouTubeId = (url: string): string | null => {
 const patterns = [
 /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,
 ];
 for (const pattern of patterns) {
 const match = url.match(pattern);
 if (match) return match[1];
 }
 return null;
};

const LiveStreamSection = () => {
 const { user, profile, isAdmin } = useAuth();
 const { isSubscriber } = useSubscription();
 const [stream, setStream] = useState<LiveStream | null>(null);
 const [hasAccess, setHasAccess] = useState(false);
 const [loading, setLoading] = useState(true);
 const [showPayment, setShowPayment] = useState(false);
 const [videoDuration, setVideoDuration] = useState(0);
 const getCurrentTimeRef = useRef<(() => number) | null>(null);
 const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null);

 useEffect(() => {
   getDeviceId().then(setCurrentDeviceId);
 }, []);

 useEffect(() => {
 fetchStream();
 }, [user]);

 const fetchStream = async () => {
 setLoading(true);
 const { data } = await supabase
 .from("live_streams")
 .select("*")
 .eq("is_active", true)
 .limit(1)
 .maybeSingle();

 setStream(data as LiveStream | null);

 if (data && user) {
 // Check if subscriber (auto-access) or has paid access
 if (isSubscriber && (data as LiveStream).is_paid) {
 setHasAccess(true);
 } else {
 const { data: access } = await supabase
 .from("live_stream_access")
 .select("id")
 .eq("user_id", user.id)
 .eq("stream_id", (data as LiveStream).id)
 .maybeSingle();
 setHasAccess(!!access);
 }
 } else {
 setHasAccess(false);
 }
 setLoading(false);
 };

 if (loading) {
 return (
 <section className="px-4 md:px-8 pb-8">
 <div className="h-64 rounded-xl bg-muted animate-pulse" />
 </section>
 );
 }

 const isDeviceBlocked = !isAdmin && profile?.primary_device_id && currentDeviceId && profile.primary_device_id !== currentDeviceId;
 const canWatch = stream && (!stream.is_paid || hasAccess) && !isDeviceBlocked;
 const videoId = stream?.youtube_url ? extractYouTubeId(stream.youtube_url) : null;
 const userPrice = stream
 ? isSubscriber
 ? stream.price_subscriber
 : stream.price_non_subscriber
 : 0;

 return (
 <section className="px-4 md:px-8 pb-8">
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.5, delay: 0.2 }}
 className="bg-card border border-border rounded-xl overflow-hidden"
 >
 <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
 <Radio className="w-5 h-5 text-destructive animate-pulse" />
 <h2 className="font-display text-lg text-foreground">Transmissão ao Vivo</h2>
 {stream?.is_active && (
 <span className="ml-auto flex items-center gap-1.5 text-xs font-bold bg-destructive/15 text-destructive px-2.5 py-1 rounded-full">
 <span className="relative flex h-2 w-2">
 <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
 <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive" />
 </span>
 <span className="animate-pulse">AO VIVO</span>
 </span>
 )}
 </div>

 <div className="p-5">
 {!stream || !stream.is_active ? (
 <div className="flex flex-col items-center justify-center py-12 text-center">
 <Radio className="w-10 h-10 text-muted-foreground mb-3 opacity-40" />
 <p className="text-muted-foreground text-sm">
 Nenhuma transmissão ao vivo no momento
 </p>
 </div>
 ) : canWatch && videoId ? (
 <>
 <div className="w-full h-[65vh] md:h-[85vh]">
 <ImmersivePlayer 
 youtubeId={videoId} 
 title={stream.title} 
 autoplay={false} 
 className="w-full h-full"
 getCurrentTimeRef={getCurrentTimeRef}
 onDurationChange={(d) => setVideoDuration(d)}
 />
 </div>
 
 <div className="mt-4">
 <YoutubeSubscribe />
 </div>

 {/* Barra de Cortes - Apenas para Jogadores */}
 {user && profile?.is_player && (
 <div className="mt-6">
 <ClipRequestForm
 videoId={stream.id}
 videoDuration={videoDuration}
 getCurrentTime={() => getCurrentTimeRef.current?.() ?? 0}
 />
 </div>
 )}
 </>
 ) : isDeviceBlocked ? (
 <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground/10 flex flex-col items-center justify-center gap-4 p-6">
 <Lock className="w-12 h-12 text-destructive" />
 <p className="text-foreground font-semibold text-center text-destructive">
 Acesso Bloqueado
 </p>
 <p className="text-muted-foreground text-sm text-center max-w-xs">
 Sua conta já está vinculada a outro dispositivo. A visualização ao vivo é permitida apenas no aparelho original da sua conta.
 </p>
 </div>
 ) : stream.is_paid && !canWatch ? (
 <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground/10 flex flex-col items-center justify-center gap-4">
 <Lock className="w-12 h-12 text-muted-foreground" />
 <p className="text-foreground font-semibold text-center">
 Transmissão exclusiva
 </p>
 <p className="text-muted-foreground text-sm text-center max-w-xs">
 {user
 ? "Adquira o acesso para assistir esta transmissão."
 : "Faça login para assistir esta transmissão."}
 </p>
 {user && (
 <Button
 onClick={() => setShowPayment(true)}
 className="bg-gradient-primary text-primary-foreground font-semibold glow"
 >
 <Crown className="w-4 h-4 mr-2" />
 Assistir agora — R$ {userPrice.toFixed(2).replace(".", ",")}
 </Button>
 )}
 </div>
 ) : (
 <div className="flex flex-col items-center justify-center py-12 text-center">
 <p className="text-muted-foreground text-sm">
 A transmissão está ativa mas o link ainda não foi configurado.
 </p>
 </div>
 )}

 {stream?.is_active && stream.title && (
 <p className="mt-3 text-sm font-semibold text-foreground">{stream.title}</p>
 )}
 </div>
 </motion.div>

 {stream && user && (
 <LiveStreamPaymentModal
 open={showPayment}
 onClose={() => setShowPayment(false)}
 onSuccess={fetchStream}
 streamId={stream.id}
 streamTitle={stream.title}
 price={userPrice}
 userEmail={user.email || ""}
 />
 )}
 </section>
 );
};

export default LiveStreamSection;
